/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core'
import { FormBuilder, FormGroup } from '@angular/forms'
import { finalize, switchMap, takeUntil } from 'rxjs/operators'
import { Subject } from 'rxjs'

import { Role } from '@shared/models/role.model'
import { takeFormSnapshot } from '@core/helpers/take-form-snapshot'
import { Theme } from '@shared/models/theme.model'
import { InputSetting, Setting, SettingName } from '@shared/models/setting.model'
import { NotificationTransport } from '@shared/models/notification-transport.model'
import { SettingsService } from './settings.service'
import { DialogService } from '@shared/dialogs/dialog.service'
import { AuthService } from '@app/modules/auth/auth.service'
import { mapProviders } from '@app/core/constants/map-providers'
import { mapApiOptions } from '@app/core/constants/map-api-options'
import { reportsOptions } from '@core/constants/reports-options'
import { Timezone } from '@shared/models/timezone.model'
import { TrackedUnsavedForm, UnsavedFormTracker } from '@core/services/unsaved-form-tracker.service'
import { Integration } from '@shared/models/integration.model'
import { Plan, PlanName } from '@shared/models/plan.model'
import { NotificationTemplate } from '@app/shared/modules/notifications/shared/notification.model'
import { RoleType } from '@app/core/types/role.type'
import { billableAddons } from '@app/core/constants/billable-addons'

@Component({
  selector: 'lin-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SettingsFormComponent implements OnInit, OnDestroy, TrackedUnsavedForm {
  @Input() roles: Role[]
  @Input() themes: Theme[]
  @Input() teamId: number
  @Input() settings: Setting[]
  @Input() notificationsTransport: NotificationTransport[]
  @Input() timezones: Timezone[]
  @Input() integrations: Integration[]
  @Input() notificationsTemplates: NotificationTemplate[]
  @Input() areAdminSettings: boolean
  @Input() currentTimezone: Timezone
  @Input() plan: Plan
  @Input() isEditMode: boolean
  @Input() disableBilling: boolean
  @Input() hasTermsAgreement: boolean

  // TODO: remove when will API
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() translationsLanguages: any

  form: FormGroup
  formError: string
  isSubmitting = false
  isFormChanged: ReturnType<typeof takeFormSnapshot>

  readonly mapProviders = mapProviders
  readonly mapApiOptions = mapApiOptions
  readonly reportsOptions = reportsOptions
  readonly billableAddons = billableAddons
  readonly PlanName = PlanName
  columnLayout: 'double' | 'triple'

  private readonly onDestroy$ = new Subject<void>()

  constructor(
    private fb: FormBuilder,
    private settingsService: SettingsService,
    private dialog: DialogService,
    private unsavedFormTrackerService: UnsavedFormTracker,
    private readonly authService: AuthService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.columnLayout = this.areAdminSettings ? 'double' : 'triple'

    this.unsavedFormTrackerService.track(this)
    this.createForm()
  }

  private createForm(): void {
    const toDefaultGroup = (group: { [key: number]: boolean }, { id }: Role | NotificationTransport) => {
      group[id] = false

      return group
    }
    const defaultRolesGroup: { [key: number]: boolean } = this.roles.reduce(toDefaultGroup, {})
    const defaultNotificationsGroup: { [key: number]: boolean } = this.notificationsTransport.reduce(toDefaultGroup, {})

    this.form = this.fb.group({
      roles: this.fb.group(defaultRolesGroup),
      notificationsTransport: this.fb.group(defaultNotificationsGroup),
      theme: '',
      translation: '',
      notificationTemplate: 8,
      mapProvider: '',
      mapApiOptions: [],
      loginWithId: '',
      currentTimezone: '',
      trackingLink: false,
      asset: false,
      messenger: false,
      enableSOSInApp: false,
      enableNewDigitalForms: false,
      hideMapInApp: false,
      hideDrivingBehaviorInApp: false,
      hideFormsOnTheDashboardsInMobileApp: false,
      driverAutoLogoutOption: false,
      tripCode: false,
      disallowDriverLoginWebapp: false,
      integrations: [],
      billing: false,
      reportsOptions: [],
      userTermsAcceptance: false,
      billableAddons: [],
      cameras: false,
      bma: false,
      isBlockout: false,
    })

    this.setFormValues()

    this.form.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe(() => this.onRolesValueChanged())

    const user = this.authService.getCurrentUser()
    if (!(user.isAdmin() && [<string>RoleType.SuperAdmin, <string>RoleType.Admin].includes(user.role.name))) {
      this.form.get('notificationTemplate').disable()
    }

    !this.isEditMode && this.form.disable()

    this.isFormChanged = takeFormSnapshot(this.form)
  }

  private setFormValues(): void {
    const isOtpWithRoles = ({ name, role }: Setting) => name === SettingName.OTP && role
    const isCurrentNotificationTransport = (name: string) => (notificationTransport: NotificationTransport) =>
      notificationTransport.name === name
    const createNotificationGroup = (group: { [key: string]: boolean }, { name, value }: Setting) => {
      const currentNotificationTransportId = this.notificationsTransport.find(isCurrentNotificationTransport(name)).id

      group[currentNotificationTransportId] = value

      return group
    }
    const createRolesGroup = (group: { [key: string]: boolean }, { role, value }: Setting) => {
      group[role.id] = value

      return group
    }
    const isName =
      (currentName: [SettingName?, SettingName?, SettingName?]) =>
      ({ name }: Setting) =>
        currentName.includes(name)

    const rolesGroup: { [key: string]: boolean } = this.settings.filter(isOtpWithRoles).reduce(createRolesGroup, {})
    const notificationsTransportGroup: { [key: string]: boolean } = this.settings
      .filter(isName([SettingName.SMS, SettingName.Email, SettingName.InApp]))
      .reduce(createNotificationGroup, {})
    const notificationTemplate: Setting = this.settings.find(isName([SettingName.NotificationTemplate]))
    const selectedTheme: Setting = this.settings.find(isName([SettingName.Theme]))
    const selectedProvider: Setting = this.settings.find(isName([SettingName.Provider]))
    const selectedOptions: Setting = this.settings.find(isName([SettingName.MapApiOptions]))
    const selectedTimezone: Setting = this.settings.find(isName([SettingName.Timezone]))
    const selectedLoginWithId: Setting = this.settings.find(isName([SettingName.LoginWithId]))
    const selectedTrackingLink: Setting = this.settings.find(isName([SettingName.TrackingLink]))
    const selectedEnableSOSInApp: Setting = this.settings.find(isName([SettingName.EnableSOSInApp]))
    const selectedHideMapInApp: Setting = this.settings.find(isName([SettingName.HideMapInApp]))
    const selectedIntegrations: Setting = this.settings.find(isName([SettingName.Integrations]))
    const selectedHideDrivingBehaviorInApp: Setting = this.settings.find(isName([SettingName.HideDrivingBehaviorInApp]))
    const selectedHideFormsOnTheDashboardsInMobileApp: Setting = this.settings.find(
      isName([SettingName.HideFormsOnTheDashboardsInMobileApp]),
    )
    const selectedAsset: Setting = this.settings.find(isName([SettingName.Asset]))
    const selectedMessenger: Setting = this.settings.find(isName([SettingName.Messenger]))
    const selectedEnableNewDigitalInspectionForms: Setting = this.settings.find(isName([SettingName.DigitalForm]))
    const selectedBilling: Setting = this.settings.find(isName([SettingName.Billing]))
    const selectedDisallowDriverLoginWebapp: Setting = this.settings.find(isName([SettingName.DisallowDriverLoginWebapp]))
    const selectedReports: Setting = this.settings.find(isName([SettingName.ReportsOptions]))
    const selectedDriverAutologout: Setting = this.settings.find(isName([SettingName.DriverAutologout]))
    const selectedTripCode: Setting = this.settings.find(isName([SettingName.TripCode]))
    const selectedUserTermsAcceptance: Setting = this.settings.find(isName([SettingName.UserTermsAcceptance]))
    const selectedAddons: Setting = this.settings.find(isName([SettingName.BillableAddons]))
    const selectedCameras: Setting = this.settings.find(isName([SettingName.Cameras]))
    const selectedBMA: Setting = this.settings.find(isName([SettingName.BMA]))
    const selectedBlockout: Setting = this.settings.find(isName([SettingName.Blockout]))

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const themeId: number = selectedTheme?.value?.id || this.themes[0].id
    const mapProviderId: number = (selectedProvider && this.mapProviders[selectedProvider.value - 1].id) || this.mapProviders[1].id
    const currentTimezoneId: number = selectedTimezone?.value || this.currentTimezone?.id
    const isTrackingLink: boolean = selectedTrackingLink?.value || false
    const isSOSEnabledInApp: boolean = selectedEnableSOSInApp?.value || false
    const isMapHiddenInApp: boolean = selectedHideMapInApp?.value || false
    const isDrivingBehaviorHiddenInApp: boolean = selectedHideDrivingBehaviorInApp?.value || false
    const isHideFormsOnTheDashboardsInMobileApp: boolean = selectedHideFormsOnTheDashboardsInMobileApp?.value || false
    const isAsset: boolean = selectedAsset?.value || false
    const isMessenger: boolean = selectedMessenger?.value || false
    const isEnabledNewDigitalInspectionForms: boolean = selectedEnableNewDigitalInspectionForms?.value || false
    const isDisallowDriverLoginWebapp: boolean = selectedDisallowDriverLoginWebapp?.value || false

    const isBilling: boolean = selectedBilling?.value || false

    const isDriverAutologout: boolean = selectedDriverAutologout?.value || false
    const isTripCode: boolean = selectedTripCode?.value || false
    const isCameras: boolean = selectedCameras?.value || false
    const isBMA: boolean = selectedBMA?.value || false
    const isBlockout: boolean = selectedBlockout?.value || false

    const isUserTermsAcceptance: boolean = selectedUserTermsAcceptance?.value || false

    const mapApiOptions: number[] | [] = selectedOptions?.value || []
    const reportsOptions: string[] | [] = selectedReports?.value || []
    const integrations: number[] = selectedIntegrations?.value || []
    const billableAddons: number[] = selectedAddons?.value || []

    this.form.patchValue({
      roles: rolesGroup,
      notificationsTransport: notificationsTransportGroup,
      theme: themeId,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      translation: this.translationsLanguages[0].id,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      notificationTemplate: notificationTemplate?.value,
      mapProvider: mapProviderId,
      mapApiOptions: mapApiOptions,
      loginWithId: selectedLoginWithId?.value,
      currentTimezone: currentTimezoneId,
      trackingLink: isTrackingLink,
      enableSOSInApp: isSOSEnabledInApp,
      enableNewDigitalForms: isEnabledNewDigitalInspectionForms,
      hideMapInApp: isMapHiddenInApp,
      hideDrivingBehaviorInApp: isDrivingBehaviorHiddenInApp,
      hideFormsOnTheDashboardsInMobileApp: isHideFormsOnTheDashboardsInMobileApp,
      driverAutoLogoutOption: isDriverAutologout,
      tripCode: isTripCode,
      asset: isAsset,
      messenger: isMessenger,
      disallowDriverLoginWebapp: isDisallowDriverLoginWebapp,
      integrations,
      billing: isBilling,
      reportsOptions: reportsOptions,
      userTermsAcceptance: isUserTermsAcceptance,
      billableAddons,
      cameras: isCameras,
      bma: isBMA,
      isBlockout: isBlockout,
    })
  }

  private onRolesValueChanged(): void {
    this.formError = ''
  }

  private prepareDataForSave(): InputSetting[] {
    const formData = this.form.value
    const dataForSave = []
    /* eslint-disable */
    const prepareRoleForSave = (role: Role) =>
      dataForSave.push(this.prepareSettingForSave(SettingName.OTP, formData.roles[role.id], role.id))
    const prepareNotificationForSave = (notification) =>
      dataForSave.push(this.prepareSettingForSave(notification.name, formData.notificationsTransport[notification.id]))
    dataForSave.push(this.prepareSettingForSave(SettingName.Theme, formData.theme))
    dataForSave.push(this.prepareSettingForSave(SettingName.Provider, formData.mapProvider))
    dataForSave.push(this.prepareSettingForSave(SettingName.MapApiOptions, formData.mapApiOptions))
    dataForSave.push(this.prepareSettingForSave(SettingName.NotificationTemplate, formData.notificationTemplate))

    if (!this.areAdminSettings) {
      dataForSave.push(this.prepareSettingForSave(SettingName.LoginWithId, formData.loginWithId))
      dataForSave.push(this.prepareSettingForSave(SettingName.TrackingLink, formData.trackingLink))
      dataForSave.push(this.prepareSettingForSave(SettingName.EnableSOSInApp, formData.enableSOSInApp))
      dataForSave.push(this.prepareSettingForSave(SettingName.HideMapInApp, formData.hideMapInApp))
      dataForSave.push(this.prepareSettingForSave(SettingName.Integrations, formData.integrations))
      dataForSave.push(this.prepareSettingForSave(SettingName.HideDrivingBehaviorInApp, formData.hideDrivingBehaviorInApp))
      dataForSave.push(
        this.prepareSettingForSave(SettingName.HideFormsOnTheDashboardsInMobileApp, formData.hideFormsOnTheDashboardsInMobileApp),
      )
      dataForSave.push(this.prepareSettingForSave(SettingName.Asset, formData.asset))
      dataForSave.push(this.prepareSettingForSave(SettingName.Messenger, formData.messenger))
      dataForSave.push(this.prepareSettingForSave(SettingName.DisallowDriverLoginWebapp, formData.disallowDriverLoginWebapp))
      dataForSave.push(this.prepareSettingForSave(SettingName.DigitalForm, formData.enableNewDigitalForms))

      dataForSave.push(this.prepareSettingForSave(SettingName.Billing, formData.billing))
      dataForSave.push(this.prepareSettingForSave(SettingName.ReportsOptions, formData.reportsOptions))
      dataForSave.push(this.prepareSettingForSave(SettingName.BillableAddons, formData.billableAddons))

      dataForSave.push(this.prepareSettingForSave(SettingName.DriverAutologout, formData.driverAutoLogoutOption))
      this.authService.getCurrentUser().isAdmin() && dataForSave.push(this.prepareSettingForSave(SettingName.TripCode, formData.tripCode))
      this.authService.getCurrentUser().isAdmin() && dataForSave.push(this.prepareSettingForSave(SettingName.Cameras, formData.cameras))
      this.authService.getCurrentUser().isAdmin() && dataForSave.push(this.prepareSettingForSave(SettingName.BMA, formData.bma))
      this.authService.getCurrentUser().isAdmin() && dataForSave.push(this.prepareSettingForSave(SettingName.Blockout, formData.isBlockout))
    }

    this.hasTermsAgreement && dataForSave.push(this.prepareSettingForSave(SettingName.UserTermsAcceptance, formData.userTermsAcceptance))

    this.currentTimezone && dataForSave.push(this.prepareSettingForSave(SettingName.Timezone, formData.currentTimezone))
    this.notificationsTransport.forEach(prepareNotificationForSave)
    this.roles.forEach(prepareRoleForSave)

    return dataForSave
    /* eslint-enable */
  }

  private prepareSettingForSave(name: string, value: string, roleId?: string): InputSetting {
    return {
      roleId: +roleId || null,
      teamId: this.teamId,
      name,
      value,
    }
  }

  saveData(): void {
    this.isSubmitting = true
    const dataForSave = this.prepareDataForSave()
    const notForSet = this.authService.getCurrentUser().team.id !== this.teamId

    this.settingsService
      .updateSettings(dataForSave, this.teamId, notForSet, true)
      .pipe(
        // Reload fresh server data so the form reflects server truth after a successful save.
        switchMap(() => this.settingsService.getSettings(this.teamId)),
        finalize(() => {
          this.isSubmitting = false
          this.cdr.markForCheck()
        }),
        takeUntil(this.onDestroy$),
      )
      .subscribe({
        next: (settings) => {
          this.settings = settings
          this.setFormValues()
          this.form.markAsPristine()
          this.cdr.markForCheck()

          this.isFormChanged = takeFormSnapshot(this.form)
        },
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-member-access
        error: (err) => this.dialog.error(err.error.errors[0].detail),
      })
  }

  ngOnDestroy(): void {
    this.unsavedFormTrackerService.discard(this)
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }
}
